// Press-kit folder drift: what the Dropbox webhook found, in the app's terms.
//
// comms.press_folder_alerts is filled by supabase/functions/dropbox-webhook,
// which re-audits a project whenever Dropbox reports that something in its tree
// moved. This module is the reading half: ordering, wording, and the two writes
// a person makes (resolve, dismiss).
//
// Kept free of JSX so the ordering and wording rules are testable on their own —
// they are what decides whether a deleted project folder or a stray subfolder is
// the first thing somebody sees.
import { supabase } from '../../supabaseClient';

const comms = supabase.schema('comms');

const PRESS_BASE = '/Architecture Brio/BRIO-press';

/**
 * A press path as somebody browsing Dropbox would name it: everything below the
 * project folder, so "Photography/Edmund Sumner" rather than the full
 * "/Architecture Brio/BRIO-press/AR_Ambika River Home/Photography/Edmund Sumner".
 *
 * The instruction has to be followed in Dropbox by hand, so it has to read like
 * the thing on screen there. Falls back to the full path if it does not sit
 * under the press tree, which is better than silently showing nothing.
 */
export function pressRelativePath(path) {
  if (!path) return '';
  const prefix = `${PRESS_BASE}/`;
  if (!path.startsWith(prefix)) return path;
  const rest = path.slice(prefix.length);
  const slash = rest.indexOf('/');
  // Only the project folder itself — nothing below it to name.
  return slash === -1 ? rest : rest.slice(slash + 1);
}

/**
 * What to do in Dropbox to undo a move, in the order a person does it: the thing
 * to grab, where it is now, where it goes back to.
 *
 * The app deliberately does not do this itself. It infers the move from a
 * deletion and a creation sharing a name, and acting on an inference by moving
 * somebody's folders is a much worse failure than describing it and being wrong.
 */
export function moveInstruction(alert) {
  const d = alert?.detail || {};
  const name = d.name || alert?.label || '';
  const from = pressRelativePath(d.fromParent);
  const to = pressRelativePath(d.toParent);
  if (!name || !from || !to) return '';
  return `In Dropbox, move “${name}” out of ${to || 'the project folder'} and back into ${from}.`;
}

// Ordered most to least serious. The index doubles as the sort key, so adding a
// kind here is all it takes to place it.
export const ALERT_KINDS = [
  {
    kind: 'project_missing',
    label: 'Folder not found',
    tone: 'error',
    // Written as what happened in Dropbox, not as what the audit concluded:
    // the person reading this is about to go and look.
    describe: (a) => {
      const name = a.label || a.detail?.expected;
      return name
        ? `The project folder ${name} is no longer in BRIO-press.`
        : 'The project folder is no longer in BRIO-press.';
    },
    hint: 'It was renamed beyond recognition, moved, or deleted.',
  },
  {
    kind: 'missing',
    label: 'Missing folder',
    tone: 'error',
    describe: a => `${a.label} is gone from Dropbox.`,
    hint: 'The schema expects it. Create it again, or drop it from the schema.',
  },
  {
    kind: 'moved',
    label: 'Moved',
    tone: 'warn',
    describe: (a) => {
      const d = a.detail || {};
      const name = d.name || a.label || 'A folder';
      const from = pressRelativePath(d.fromParent);
      const to = pressRelativePath(d.toParent);
      return from && to
        ? `${name} was moved from ${from} to ${to}.`
        : `${name} was moved.`;
    },
    hint: 'Dropbox reported it leaving one folder and appearing in another.',
  },
  {
    kind: 'renamed',
    label: 'Renamed',
    tone: 'warn',
    describe: a => `The project folder is now ${a.label}, not ${a.detail?.expected || 'the expected name'}.`,
    hint: 'The app still finds it by its code prefix, but links shared earlier point at the old name. Check Dropbox structure below offers to rename it.',
  },
  {
    kind: 'unexpected',
    label: 'Unexpected folder',
    tone: 'warn',
    describe: a => `${a.label} appeared, and the schema does not describe it.`,
    hint: 'Approve it if it belongs, or move its contents into a schema folder.',
  },
];

const BY_KIND = ALERT_KINDS.reduce((acc, k, i) => ({ ...acc, [k.kind]: { ...k, rank: i } }), {});

export function alertKind(kind) {
  return BY_KIND[kind] || { kind, label: kind, tone: 'warn', describe: a => a.label || kind, hint: '', rank: ALERT_KINDS.length };
}

export function describeAlert(alert) {
  return alertKind(alert?.kind).describe(alert || {});
}

/** Worst first; within a kind, by the path so a project reads top-down. */
export function sortAlerts(alerts = []) {
  return [...alerts].sort((a, b) =>
    alertKind(a.kind).rank - alertKind(b.kind).rank ||
    (a.path || '').localeCompare(b.path || ''));
}

export function groupAlertsByProject(alerts = []) {
  const byProject = new Map();
  for (const a of alerts) {
    if (!byProject.has(a.project_code)) byProject.set(a.project_code, []);
    byProject.get(a.project_code).push(a);
  }
  // Projects with the worst finding first, then by code, so the banner's order
  // matches the urgency it is claiming.
  return [...byProject.entries()]
    .map(([project_code, list]) => ({ project_code, alerts: sortAlerts(list) }))
    .sort((x, y) =>
      alertKind(x.alerts[0]?.kind).rank - alertKind(y.alerts[0]?.kind).rank ||
      (x.project_code || '').localeCompare(y.project_code || ''));
}

/**
 * One line for the projects banner.
 *
 * Deliberately counts projects rather than alerts: one project folder deleted
 * produces a single alert and one schema change produces eight, and the number
 * that tells you how bad the morning is, is how many projects are involved.
 */
export function summarizeAlerts(alerts = []) {
  const projects = groupAlertsByProject(alerts);
  if (projects.length === 0) return null;
  const worst = alertKind(projects[0].alerts[0]?.kind);
  const n = projects.length;
  return {
    projects,
    total: alerts.length,
    tone: worst.tone,
    text: alerts.length === 1
      ? `Dropbox folders changed in 1 project — 1 thing no longer matches the press-kit schema.`
      : `Dropbox folders changed in ${n} project${n === 1 ? '' : 's'} — ${alerts.length} things no longer match the press-kit schema.`,
  };
}

// ── Reads and writes ────────────────────────────────────────────────────────

const ALERT_COLUMNS = 'id, project_code, kind, path, label, detail, status, detected_at, last_seen_at';

/** Open alerts, for one project or for all of them. */
export async function fetchOpenFolderAlerts(projectCode = null) {
  let q = comms.from('press_folder_alerts').select(ALERT_COLUMNS).eq('status', 'open');
  if (projectCode) q = q.eq('project_code', projectCode);
  const { data, error } = await q.order('detected_at', { ascending: false });
  if (error) throw error;
  return sortAlerts(data || []);
}

/**
 * Resolve ("I've dealt with it" — the next scan reopens it if it hasn't) or
 * dismiss ("this is how the folder is meant to be" — permanent for that
 * finding). RLS allows no direct write to the table; this RPC is the only door.
 */
export async function setFolderAlertStatus(id, status) {
  const { data, error } = await comms.rpc('set_press_folder_alert_status', {
    p_alert_id: id,
    p_status: status,
  });
  if (error) throw error;
  return data;
}

/** Whether the webhook is wired up, and what it last did. */
export async function fetchDropboxWatchState() {
  const [cursor, lastEvent, pending] = await Promise.all([
    comms.from('dropbox_delta_cursor')
      .select('path, baselined_at, last_event_at, last_scan_at, last_error, entries_seen')
      .maybeSingle(),
    comms.from('dropbox_webhook_events')
      .select('id, received_at, processed_at, error, alerts_opened')
      .order('received_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    comms.from('dropbox_webhook_events')
      .select('id', { count: 'exact', head: true })
      .is('processed_at', null),
  ]);
  return {
    cursor: cursor.data || null,
    lastEvent: lastEvent.data || null,
    pending: pending.count || 0,
    // A cursor is what makes the webhook useful; without one, notifications
    // arrive and have nothing to compare against.
    watching: !!cursor.data?.baselined_at,
  };
}

// ── Webhook plumbing ────────────────────────────────────────────────────────

/**
 * The URI to register on the Dropbox app.
 *
 * Dropbox has no API for adding a webhook — it is typed into the App Console by
 * hand, once — so the setup panel's whole job is to show this string and to say
 * whether anything has arrived through it.
 */
export const DROPBOX_WEBHOOK_URL =
  `${process.env.REACT_APP_SUPABASE_URL || ''}/functions/v1/dropbox-webhook`;

/**
 * Asks the webhook function to read Dropbox now.
 *
 *   scan      catch up on changes since the stored cursor
 *   baseline  take a fresh cursor and start watching from this moment, which is
 *             how watching begins and how a cursor Dropbox has invalidated is
 *             recovered. It forgets the backlog — there is no way to ask Dropbox
 *             what happened before a cursor existed.
 */
export async function runDropboxWatchScan(mode = 'scan') {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  const res = await fetch(DROPBOX_WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ mode }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json.error || json.message || `Dropbox scan failed (HTTP ${res.status})`);
  }
  return json;
}
